import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import find from 'lodash/find';
import get from 'lodash/get';
import moment from 'moment-timezone';
import { Modal } from 'sure-components';
import Button from 'shared/lib/components/Button';
import {
  getClaimsHistory,
  showGlobalModal,
  hideGlobalModal,
} from '../../store/actions';
import IconCircle from 'shared/lib/components/IconCircle';
import Spinner from 'shared/lib/components/Spinner';
import * as analytics from 'shared/lib/analytics';
import './Claim.css';

class Claim extends Component {
  componentDidMount() {
    document.title = `Claim ${this.props.params.claimId} - Sure`;
    this.props.getClaimsHistory().then(() => {
      analytics.page('Portal - Claim Detail', {
        category: 'claim',
        label: `${this.claim.status}:${get(
          this.claim,
          'resolution.resolution_type'
        )}`,
      });
    });
  }

  get claim() {
    return find(this.props.claims, { claim_id: this.props.params.claimId });
  }

  get secondaryIcon() {
    if (this.claim.status === 'handed_off') {
      return null;
    } else if (this.claim.status === 'submitted') {
      return 'search';
    } else if (get(this.claim, 'resolution.resolution_type') === 'rejected') {
      return 'close';
    } else if (get(this.claim, 'resolution.resolution_type') === 'approved') {
      return 'check';
    } else {
      return 'help_outline';
    }
  }

  get badgeBackground() {
    if (get(this.claim, 'resolution.resolution_type') === 'approved') {
      return 'green';
    } else if (get(this.claim, 'resolution.resolution_type') === 'rejected') {
      return 'red';
    } else {
      return 'blue';
    }
  }

  get heading() {
    if (this.claim.status === 'handed_off') {
      return 'Please call Sure at 1-844-335-5441 to begin your mail-away repair claim.';
    } else if (this.claim.status === 'submitted') {
      return 'Your claim is in review';
    } else if (get(this.claim, 'resolution.resolution_type') === 'rejected') {
      return 'Claim rejected';
    } else if (get(this.claim, 'resolution.resolution_type') === 'approved') {
      return 'Claim approved';
    } else {
      return 'Unknown claim status';
    }
  }

  get subheading() {
    if (this.claim.status === 'handed_off') {
      return 'You will need the following information to begin your claim';
    } else if (this.claim.status === 'submitted') {
      return 'You will receive an email and payment pending the approval of your claim.';
    } else if (get(this.claim, 'resolution.resolution_type') === 'rejected') {
      return 'Your claim has been rejected.';
    } else if (get(this.claim, 'resolution.resolution_type') === 'approved') {
      return 'Your claim has been approved.';
    } else {
      return null;
    }
  }

  get loadingStateNode() {
    return (
      <div className="Claim-loadingState Container Container--small">
        <Spinner />
        <h1 className="Claim-heading">Fetching claim...</h1>
      </div>
    );
  }

  get inStoreSwitchNode() {
    const linkTo = `/account/claims/new/devices/policies/${
      this.claim.policy_number
    }`;
    return (
      <div>
        <a
          className="Claim-switchLink"
          href
          onClick={this.props.showConfirmModal}
        >
          {'Want to switch to an in-store repair?'}
        </a>
        <Modal
          className="Claim-switchModal"
          title="In-store repair"
          isActive={this.props.isModalActive}
          onDismiss={this.props.hideGlobalModal}
        >
          <p>
            You will need to restart the claims process for your phone if you
            switch to an in-store repair for your phone.
          </p>
          <Button to={linkTo} onClick={this.handleModalConfirmation.bind(this)}>
            Ok
          </Button>
        </Modal>
      </div>
    );
  }

  get claimNode() {
    return (
      <div className="Container Container--small">
        <div className="Claim-section">
          <IconCircle
            className="Claim-icon"
            icon="assignment"
            size={80}
            color="medium"
            background="gray"
            badgeIcon={this.secondaryIcon}
            badgeColor="light"
            badgeBackground={this.badgeBackground}
          />
          <h1 className="Claim-heading">{this.heading}</h1>
          <p className="Claim-subheading">{this.subheading}</p>
        </div>
        <div className="Claim-section">
          <h2 className="Claim-section-heading">Claim Details</h2>
          <p className="Claim-section-detail">
            Claim ID #{this.props.params.claimId}
          </p>
        </div>
        <div className="Claim-section">
          <h2 className="Claim-section-heading">Coverage Details</h2>
          <p className="Claim-section-detail">
            {this.claim.policy_details.plan_name} Smartphone
            <br />
            Coverage #{this.claim.policy_number}
            <br />
            Purchased{' '}
            {moment(this.claim.policy_details.created).format('MMM D, YYYY')}
            <br />
            {this.claim.policy_details.device_model}
          </p>
        </div>
        <div className="Claim-section">
          <h2 className="Claim-section-heading">Damage Details</h2>
          <p className="Claim-section-detail">
            {this.claim.claim_details.damage_description}
          </p>
          <p className="Claim-section-detail">
            {this.claim.claim_details.claim_description}
          </p>
        </div>
        {this.claim.status === 'handed_off' && this.inStoreSwitchNode}
      </div>
    );
  }

  get notFoundNode() {
    return (
      <div className="Claim-notFoundState Container Container--small">
        <h1 className="Claim-heading">Claim not found</h1>
      </div>
    );
  }

  get contentNode() {
    if (this.props.isFetchingClaims) {
      return this.loadingStateNode;
    } else if (this.claim) {
      return this.claimNode;
    } else {
      return this.notFoundState;
    }
  }

  handleModalConfirmation() {
    analytics.track('Portal - Claim Switch In-Store Confirm Clicked', {
      category: 'claim',
    });
    this.props.hideGlobalModal();
  }

  render() {
    return <div className="Claim">{this.contentNode}</div>;
  }
}

const mapStateToProps = state => {
  return {
    claims: state.claims,
    isFetchingClaims: state.isFetchingClaims,
    isModalActive: state.activeGlobalModal === 'inStoreRepair',
  };
};

const mapDispatchToProps = dispatch => {
  return {
    getClaimsHistory: () => dispatch(getClaimsHistory()),
    showConfirmModal: e => {
      e.preventDefault();
      analytics.track('Portal - Claim Send By Mail Switch In-Store Clicked', {
        category: 'claim',
      });
      return dispatch(
        showGlobalModal({
          id: 'inStoreRepair',
        })
      );
    },
    hideGlobalModal: () => dispatch(hideGlobalModal()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(Claim));
